import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';

type PriceSource = 'negotiated' | 'offer' | 'list';

interface PriceSegment {
  id: string;
  kind: 'offer' | 'negotiated';
  amount: number;
  fromPeriod: string;
  untilPeriod: string | null;
  offerKey?: string;
  couponCode?: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
  noticeSentAt?: string;
}

interface Schedule {
  companyName: string;
  planKey: string;
  planName: string;
  planFound: boolean;
  listAmount: number | null;
  isPartner: boolean;
  chargePeriod: string;
  segments: PriceSegment[];
  entitlementOverrides: { add?: string[]; remove?: string[] } | null;
  entitlements: { all: boolean; keys: string[]; denied: string[] };
  periods: Array<{ period: string; amount: number; source: PriceSource; label: string }>;
}

interface Feature {
  key: string;
  label: string;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPeriod = (p: string) => `${p.slice(5, 7)}/${p.slice(0, 4)}`;

const SOURCE: Record<PriceSource, { label: string; color: 'success' | 'warning' | 'primary' }> = {
  negotiated: { label: 'Negociado', color: 'success' },
  offer: { label: 'Oferta', color: 'warning' },
  list: { label: 'Preço do plano', color: 'primary' }
};

interface Props {
  tenantId: string | null;
  onClose: () => void;
}

/** Preço do cliente (schema vbody): cronograma calculado pelo produto, preço negociado e acesso. */
export const TenantPricingDialog: React.FC<Props> = ({ tenantId, onClose }) => {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [catalog, setCatalog] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useMessage();

  // Novo preço negociado
  const [amount, setAmount] = useState('');
  const [fromPeriod, setFromPeriod] = useState('');
  const [hasEnd, setHasEnd] = useState(false);
  const [untilPeriod, setUntilPeriod] = useState('');
  const [reason, setReason] = useState('');

  // Encerrar negociado
  const [ending, setEnding] = useState<{ id: string; until: string } | null>(null);

  // Funcionalidades do cliente
  const [addKeys, setAddKeys] = useState<string[]>([]);
  const [removeKeys, setRemoveKeys] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [sched, features] = await Promise.all([
        apiService.getTenantPricingSchedule(tenantId, 12),
        apiService.getFeatureCatalog().catch(() => ({ data: { data: [] } }))
      ]);
      const data: Schedule = sched.data.data;
      setSchedule(data);
      setCatalog(features.data?.data ?? []);
      setAddKeys(data.entitlementOverrides?.add ?? []);
      setRemoveKeys(data.entitlementOverrides?.remove ?? []);
      setFromPeriod(prev => prev || data.chargePeriod);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível consultar o preço deste cliente.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    setSchedule(null);
    setAmount('');
    setFromPeriod('');
    setHasEnd(false);
    setUntilPeriod('');
    setReason('');
    setEnding(null);
    load();
  }, [tenantId, load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setSaving(true);
    try {
      await fn();
      showSuccess(ok);
      await load();
      return true;
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Erro ao salvar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addNegotiated = async () => {
    if (!tenantId) return;
    const done = await run(
      () =>
        apiService.addTenantNegotiatedPrice(tenantId, {
          amount: Number(amount.replace(',', '.')),
          fromPeriod,
          untilPeriod: hasEnd ? untilPeriod : null,
          reason
        }),
      'Preço negociado definido'
    );
    if (done) {
      setAmount('');
      setReason('');
      setHasEnd(false);
      setUntilPeriod('');
    }
  };

  const endNegotiated = async () => {
    if (!tenantId || !ending) return;
    const done = await run(
      () => apiService.endTenantNegotiatedPrice(tenantId, ending.id, ending.until),
      'Preço negociado encerrado'
    );
    if (done) setEnding(null);
  };

  const saveEntitlements = () =>
    tenantId &&
    run(
      () => apiService.setTenantEntitlements(tenantId, { add: addKeys, remove: removeKeys }),
      'Funcionalidades do cliente salvas'
    );

  const featureLabel = (key: string) => catalog.find(f => f.key === key)?.label ?? key;
  const negotiatedValid =
    Number(amount.replace(',', '.')) > 0 &&
    !!fromPeriod &&
    reason.trim().length > 0 &&
    (!hasEnd || (!!untilPeriod && untilPeriod >= fromPeriod));

  return (
    <Dialog open={!!tenantId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preço do cliente{schedule ? `: ${schedule.companyName}` : ''}</DialogTitle>
      <DialogContent>
        {loading && !schedule && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Consultando o produto...</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {schedule && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip label={`Plano ${schedule.planName}`} />
              {schedule.listAmount != null && (
                <Chip variant="outlined" label={`Preço do plano ${brl(schedule.listAmount)}`} />
              )}
              <Chip
                variant="outlined"
                label={`Competência atual ${fmtPeriod(schedule.chargePeriod)}`}
              />
              {schedule.isPartner && <Chip color="success" label="Parceiro: não é cobrado" />}
            </Stack>
            {!schedule.planFound && (
              <Alert severity="error">
                O plano "{schedule.planKey}" não existe mais no catálogo. A cobrança deste cliente
                vai falhar até o plano ser corrigido.
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Próximas 12 competências
              </Typography>
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Competência</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Origem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schedule.periods.map(p => (
                      <TableRow key={p.period}>
                        <TableCell>{fmtPeriod(p.period)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {brl(p.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={SOURCE[p.source].color}
                            label={p.label || SOURCE[p.source].label}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Contrato
              </Typography>
              {schedule.segments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sem oferta nem preço negociado: paga o preço do plano.
                </Typography>
              )}
              <Stack spacing={1}>
                {schedule.segments.map(s => (
                  <Box
                    key={s.id}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1
                    }}
                  >
                    <Chip
                      size="small"
                      color={s.kind === 'negotiated' ? 'success' : 'warning'}
                      label={
                        s.kind === 'negotiated'
                          ? 'Negociado'
                          : `Oferta ${s.offerKey ?? ''}${s.couponCode ? ` · cupom ${s.couponCode}` : ''}`
                      }
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {brl(s.amount)}
                    </Typography>
                    <Typography variant="body2">
                      {fmtPeriod(s.fromPeriod)} até{' '}
                      {s.untilPeriod ? fmtPeriod(s.untilPeriod) : 'sem fim'}
                    </Typography>
                    {s.reason && (
                      <Typography variant="body2" color="text.secondary">
                        · {s.reason}
                      </Typography>
                    )}
                    {s.noticeSentAt && (
                      <Typography variant="caption" color="text.secondary">
                        · avisado em {new Date(s.noticeSentAt).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {s.kind === 'negotiated' &&
                      (ending?.id === s.id ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            id={`end-${s.id}`}
                            type="month"
                            size="small"
                            label="Última competência"
                            value={ending.until}
                            onChange={e => setEnding({ id: s.id, until: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                          <Button size="small" onClick={() => setEnding(null)}>
                            Cancelar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            disabled={!ending.until || saving}
                            onClick={endNegotiated}
                          >
                            Encerrar
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="small"
                          onClick={() =>
                            setEnding({ id: s.id, until: s.untilPeriod ?? schedule.chargePeriod })
                          }
                        >
                          Encerrar
                        </Button>
                      ))}
                  </Box>
                ))}
              </Stack>
              {ending && (
                <Typography variant="caption" color="text.secondary">
                  Se a última competência for anterior ao início, o preço negociado é removido.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Definir preço negociado
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    id="neg-amount"
                    label="Mensalidade"
                    type="number"
                    size="small"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>
                    }}
                    inputProps={{ min: 0.01, step: 0.01 }}
                  />
                  <TextField
                    id="neg-from"
                    label="A partir de"
                    type="month"
                    size="small"
                    value={fromPeriod}
                    onChange={e => setFromPeriod(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControlLabel
                    control={
                      <Switch checked={hasEnd} onChange={e => setHasEnd(e.target.checked)} />
                    }
                    label="Com data de fim"
                  />
                  {hasEnd && (
                    <TextField
                      id="neg-until"
                      label="Até (inclusive)"
                      type="month"
                      size="small"
                      value={untilPeriod}
                      onChange={e => setUntilPeriod(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </Stack>
                <TextField
                  id="neg-reason"
                  label="Motivo"
                  size="small"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Ex.: presente parceria, negociação comercial"
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={addNegotiated}
                    disabled={!negotiatedValid || saving}
                  >
                    Definir preço negociado
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  O preço negociado tem prioridade sobre a oferta e o preço do plano, e continua
                  valendo se o cliente trocar de plano. Para isentar, marque o cliente como
                  parceiro.
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Funcionalidades deste cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Hoje:{' '}
                {schedule.entitlements.all
                  ? schedule.entitlements.denied.length
                    ? `acesso total, exceto ${schedule.entitlements.denied.map(featureLabel).join(', ')}`
                    : 'acesso total'
                  : schedule.entitlements.keys.map(featureLabel).join(', ') || 'nenhuma'}
              </Typography>
              {catalog.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Cadastre funcionalidades na aba de precificação para ajustar o acesso por cliente.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={catalog.map(f => f.key)}
                    getOptionLabel={featureLabel}
                    value={addKeys}
                    onChange={(_, v) => {
                      setAddKeys(v);
                      setRemoveKeys(prev => prev.filter(k => !v.includes(k)));
                    }}
                    renderInput={params => (
                      <TextField
                        {...(params as React.ComponentProps<typeof TextField>)}
                        size="small"
                        label="Liberar além do plano"
                      />
                    )}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={catalog.map(f => f.key)}
                    getOptionLabel={featureLabel}
                    value={removeKeys}
                    onChange={(_, v) => {
                      setRemoveKeys(v);
                      setAddKeys(prev => prev.filter(k => !v.includes(k)));
                    }}
                    renderInput={params => (
                      <TextField
                        {...(params as React.ComponentProps<typeof TextField>)}
                        size="small"
                        label="Retirar deste cliente"
                      />
                    )}
                  />
                  <Box>
                    <Button variant="outlined" onClick={saveEntitlements} disabled={saving}>
                      Salvar funcionalidades
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    O produto aplica a mudança em até 1 minuto.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};
